import React, { useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { useDispatch, useSelector } from 'react-redux';
import { getSermon } from '../../Redux/slices/sermonSlice';
import Header from '../Common/Header';
import Footer from '../Common/Footer';
import { FaCalendar, FaUser, FaClock, FaTag, FaPlay, FaArrowLeft } from 'react-icons/fa';
import readingImage from '../../Assets/Reading.png';
import prophetessImage from '../../Assets/Prophetess.png';

const SermonDetails = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const { currentSermon, loading, error } = useSelector((state) => state.sermons);

  useEffect(() => {
    if (id) {
      dispatch(getSermon(id));
    }
  }, [id, dispatch]);

  const formatDate = (dateString) => {
    const date = new Date(dateString);
    return date.toLocaleDateString('en-US', { year: 'numeric', month: 'long', day: 'numeric' });
  };

  const handleClose = () => {
    navigate('/sermon', { replace: false });
  };

  if (loading) {
    return (
      <>
        <Header />
        <div className="min-h-screen flex items-center justify-center">
          <div className="text-center">
            <div className="inline-block animate-spin rounded-full h-12 w-12 border-b-2 border-primary"></div>
            <p className="mt-4 text-gray-600">Loading sermon...</p>
          </div>
        </div>
        <Footer />
      </>
    );
  }

  if (error || !currentSermon) {
    return (
      <>
        <Header />
        <div className="min-h-screen flex items-center justify-center">
          <div className="text-center">
            <p className="text-red-600 text-lg mb-4">{error || 'Sermon not found'}</p>
            <button 
              onClick={() => navigate('/sermon')}
              className="bg-primary text-white px-6 py-3 rounded-lg hover:bg-opacity-90"
            >
              Back to Sermons
            </button>
          </div>
        </div>
        <Footer />
      </>
    );
  }

  return (
    <>
      <Header />
      
      {/* Hero Section */}
      <div 
        className="relative bg-cover bg-center min-h-[400px] flex items-end"
        style={{ 
          backgroundImage: `linear-gradient(rgba(0, 0, 0, 0.6), rgba(0, 0, 0, 0.6)), url(${currentSermon.thumbnailUrl || readingImage})`,
        }}
      >
        <div className="container mx-auto px-6 md:px-12 lg:px-24 pb-12">
          <div className="flex items-center gap-2 mb-4">
            <span className="bg-primary text-white text-xs px-3 py-1 rounded-full font-medium font-sf-pro">
              {currentSermon.sermonType || 'Regular'}
            </span>
            <span className="bg-white/20 text-white text-xs px-3 py-1 rounded-full font-medium font-sf-pro">
              {currentSermon.category}
            </span>
          </div>
          <h1 className="text-4xl md:text-5xl font-bold text-white mb-4">{currentSermon.title}</h1>
          <div className="flex flex-wrap items-center gap-4 text-white">
            <div className="flex items-center gap-2">
              <FaUser className="text-sm" />
              <span className="font-sf-pro">{currentSermon.preacher}</span>
            </div>
            <div className="flex items-center gap-2">
              <FaCalendar className="text-sm" />
              <span className="font-sf-pro">{formatDate(currentSermon.date)}</span>
            </div>
            {currentSermon.duration && (
              <div className="flex items-center gap-2">
                <FaClock className="text-sm" />
                <span className="font-sf-pro">{currentSermon.duration}</span>
              </div>
            )}
          </div>
        </div>
      </div>

      {/* Back to Sermons Button */}
      <div className="bg-gray-50 pt-8 pb-4">
        <div className="container mx-auto px-6 md:px-12 lg:px-24">
          <button 
            type="button"
            onClick={handleClose}
            className="flex items-center gap-2 text-primary hover:text-opacity-80 transition font-sf-pro font-medium cursor-pointer"
          >
            <FaArrowLeft className="text-sm" />
            <span>Back to Sermons</span>
          </button>
        </div>
      </div>

      {/* Sermon Content Section */}
      <div className="bg-gray-50 pb-16">
        <div className="container mx-auto px-6 md:px-12 lg:px-24">

          {/* Video Thumbnail Section */}
          <div className="bg-black rounded-lg overflow-hidden mb-8 relative group cursor-pointer">
            <div className="aspect-video relative">
              {/* Thumbnail Image */}
              <img 
                src={currentSermon.thumbnailUrl || readingImage} 
                alt={currentSermon.title}
                className="w-full h-full object-cover"
              />
              
              {/* Overlay */}
              <div className="absolute inset-0 bg-black bg-opacity-40 group-hover:bg-opacity-50 transition flex items-center justify-center">
                {currentSermon.youtubeUrl ? (
                  <button
                    onClick={() => window.open(currentSermon.youtubeUrl, '_blank')}
                    className="transform group-hover:scale-110 transition"
                  >
                    <div className="w-20 h-20 bg-red-600 rounded-full flex items-center justify-center shadow-lg hover:bg-red-700 transition">
                      <FaPlay className="text-white text-2xl ml-1" />
                    </div>
                  </button>
                ) : (
                  <div className="text-center text-white">
                    <p className="text-lg font-sf-pro">Video not available</p>
                  </div>
                )}
              </div>
              
              {/* Watch on YouTube Badge */}
              {currentSermon.youtubeUrl && (
                <div className="absolute bottom-4 left-4 bg-red-600 text-white px-4 py-2 rounded-lg font-medium font-sf-pro text-sm">
                  Click to Watch on YouTube
                </div>
              )}
            </div>
          </div>

          {/* About This Message Section */}
          <div className="bg-white rounded-lg p-8 shadow-sm mb-8">
            <h3 className="text-2xl font-bold mb-4">About This Message</h3>
            <p className="text-gray-700 leading-relaxed font-sf-pro mb-6">
              {currentSermon.description}
            </p>
            
            {/* Sermon Details */}
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6 pt-6 border-t border-gray-200">
              <div>
                <h4 className="font-semibold text-gray-900 mb-2">Preacher</h4>
                <div className="flex items-center gap-3">
                  <img 
                    src={prophetessImage} 
                    alt={currentSermon.preacher} 
                    className="w-12 h-12 rounded-full object-cover" 
                  />
                  <span className="font-sf-pro">{currentSermon.preacher}</span>
                </div>
              </div>
              
              {currentSermon.scripture && (
                <div>
                  <h4 className="font-semibold text-gray-900 mb-2">Scripture Reference</h4>
                  <p className="text-gray-700 font-sf-pro">{currentSermon.scripture}</p>
                </div>
              )}
              
              <div>
                <h4 className="font-semibold text-gray-900 mb-2">Date</h4>
                <p className="text-gray-700 font-sf-pro">{formatDate(currentSermon.date)}</p>
              </div>
              
              {currentSermon.tags && currentSermon.tags.length > 0 && (
                <div>
                  <h4 className="font-semibold text-gray-900 mb-2">Tags</h4>
                  <div className="flex flex-wrap gap-2">
                    {currentSermon.tags.map((tag, index) => (
                      <span 
                        key={index}
                        className="bg-gray-100 text-gray-700 px-3 py-1 rounded-full text-sm font-sf-pro"
                      >
                        <FaTag className="inline mr-1 text-xs" />
                        {tag}
                      </span>
                    ))}
                  </div>
                </div>
              )}
            </div>
          </div>
        </div>
      </div>

      <Footer />
    </>
  );
};

export default SermonDetails;